/**
 * Coordinate validation helpers for MapKit map surfaces.
 *
 * MapKit throws `TypeError: [MapKit] Expected a number for x in MapRect
 * constructor but got NaN` when a `Coordinate`/`CoordinateRegion`/`MapRect`
 * is built from a NaN or non-finite value.  A bare `!= null` check lets NaN
 * through (`NaN != null` is `true`), and a bare falsy check (`!lat`) rejects
 * the valid equatorial coordinate `0`.
 *
 * Centralise the guard here so every map surface uses the same rule and the
 * behaviour can be covered by a single test suite.
 */

/**
 * Coerce an unknown value to a finite number, or return `null`.
 *
 * Mirrors the `asNum` helper in `lib/trip-location.ts` so both helpers share
 * the same promotion rules:
 *   • null / undefined   → null  (loose `== null` catches both)
 *   • ""  (empty string) → null  (`parseFloat("")` = NaN → non-finite)
 *   • NaN / Infinity     → null
 *   • 0                  → 0     (valid — falsy checks would wrongly reject this)
 *   • "51.5"             → 51.5  (numeric strings accepted)
 *   • "abc"              → null
 *
 * Using `parseFloat` (not `Number`) so that `""` → NaN rather than `0`.
 */
function toFinite(v: unknown): number | null {
  if (v == null) return null;
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
}

/**
 * Returns `true` only when both lat and lng are finite real numbers — no NaN,
 * Infinity, null, undefined, or non-numeric strings.
 *
 * Use for any coordinate pair before constructing a MapKit `Coordinate`,
 * `CoordinateRegion`, or `MapRect`.
 */
export function isFiniteCoord(lat: unknown, lng: unknown): boolean {
  return toFinite(lat) !== null && toFinite(lng) !== null;
}

/**
 * Returns `true` when the pair is finite AND within the geographic coordinate
 * range (lat: −90 to +90, lng: −180 to +180).
 *
 * Use when an out-of-range value would cause incorrect map placement (e.g.
 * multi-car `geoCars` filtering where a single outlier distorts the camera
 * bounding box).
 */
export function isValidCoord(lat: unknown, lng: unknown): boolean {
  const la = toFinite(lat);
  const lo = toFinite(lng);
  return (
    la !== null &&
    lo !== null &&
    la >= -90 &&
    la <= 90 &&
    lo >= -180 &&
    lo <= 180
  );
}
